import { Favorite, FavoriteBorder } from "@mui/icons-material";
import { Checkbox } from "@mui/material";
import React, { ChangeEvent, ReactElement, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import FullAsset from "../../components/dialogs/full-asset";
import {
  Asset as GqlAsset,
  UpdateAssetInput,
} from "../../generated/gql/graphql";
import useUpdateAsset from "../../hooks/mutation/category/use-update-asset";
import useAsset from "../../hooks/query/asset/use-asset";
import {
  getDirectusAssetsPath,
  useDirectus,
} from "../../providers/DirectusProvider";
import AssetPreview from "../../sections/detail/asset-preview";
import AssetPropertyDetails from "../../sections/detail/asset-property-details";
import Header from "../../sections/detail/header";
import StackVersionSelector from "../../sections/detail/stack-version-selector";
import Asset from "../../services/asset";
import { AssetType } from "../../types/assets";

import useDetailPage from "./hooks/useDetailPage";
import PDFViewer from "./parts/pdf-viewer";

export default function DetailPage(): ReactElement {
  const { id } = useParams();

  const { setFavorite } = useDetailPage();

  const { data, loading, error, refetch } = useAsset(id || "");
  const [asset, setAsset] = useState<GqlAsset>();
  const [type, setType] = useState<string>();
  const [isEdit, setIsEdit] = useState(false);
  const [update, setUpdate] = useState<UpdateAssetInput | undefined>();

  const { directus } = useDirectus();
  const { updateAsset } = useUpdateAsset();
  const { getFileType } = Asset(directus);

  useEffect(() => {
    if (!data || !data.asset) {
      return;
    }

    setAsset(data.asset);

    if (data.asset.image) {
      getFileType(data.asset.image).then((result) => {
        setType(result?.type);
      });
    }
  }, [data]);

  const [modal, setModal] = useState<boolean>(false);

  const handleToggleModal = (): void => {
    if (AssetType.pdf === type || AssetType.psd === type) {
      // Note(note): Not compatible full preview asset types
      return;
    }
    setModal(!modal);
  };

  const handleAssetChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const {
      target: { value, name },
    } = event;

    setUpdate({
      ...update,
      [name]: value,
    });
  };

  async function handleAssetUpdate(): Promise<void> {
    if (!id || !update) {
      return;
    }

    try {
      await updateAsset(id, update);
      await refetch();
    } catch (err) {
      console.error(err);
    }
  }

  if (!asset || loading) {
    return (
      <div className="flex flex-col shadow rounded-[15px] p-50 bg-white basis-[100%] h-fit">
        <div className="flex flex-col space-y-10 bg-white basis-[100%] max-w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col shadow rounded-[15px] p-50 bg-white basis-[100%] h-fit">
      <div className="flex flex-col space-y-10 bg-white basis-[100%] max-w-full">
        <Header
          isEdit={isEdit}
          asset={asset}
          setIsEdit={setIsEdit}
          onUpdate={handleAssetUpdate}
          setUpdate={setUpdate}
        />
        <div className="flex w-full">
          <div
            // className={`${
            //     messageNavigation ? "w-2/5" : "w-1/2"
            // } bg-slate-200 relative flex justify-center items-center hover:bg-slate-400 group hover:is-hover`}
            className="bg-slate-200 w-1/2 relative flex justify-center items-center hover:bg-slate-400 group hover:is-hover"
          >
            <Checkbox
              className="absolute right-20 top-20 bg-white z-10"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setFavorite(event.target.checked);
                (async function () {
                  if (asset?.id) {
                    await directus
                      ?.items("metaphore_assets")
                      .updateOne(asset?.id, {
                        favorite: event.target.checked,
                      });
                  }
                })();
              }}
              checked={asset.favorite || false}
              icon={<FavoriteBorder />}
              checkedIcon={<Favorite />}
            />
            {asset && <StackVersionSelector asset={asset} />}
            <div onClick={handleToggleModal}>
              <AssetPreview asset={asset} />
            </div>
          </div>
          <AssetPropertyDetails
            isEdit={isEdit}
            update={update}
            asset={asset}
            onChange={handleAssetChange}
          />
        </div>
      </div>
      <FullAsset isOpen={modal} onClose={handleToggleModal}>
        {AssetType.pdf === type ? (
          <PDFViewer />
        ) : (
          <img
            className="inline-block object-contain"
            src={`${getDirectusAssetsPath(directus)}/${
              asset?.image
            }?width=2000`}
            alt={`full preview ${asset.name}`}
          />
        )}
      </FullAsset>
    </div>
  );
}
